import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { Spinner } from '@connect-x-app/web-ui';
import { Row, Table } from '@tanstack/react-table';

import { AddButton, EmptyState, TableLayout } from '../../../shared';
import { ConfirmationModal } from '../../../shared/confirmationModal/ConfirmationModal';
import { customModalStyles } from '../../../shared/modal/ModalUtils';
import { IndexableQuickReply } from '../models/indexableQuickReply';
import { QuickReplyModal } from './QuickReplyModal';

type Props = {
  deletePermission: boolean;
  isLoading: boolean;
  isLoadingDeleteQuickReplies: boolean;
  isLoadingUpdateQuickReplies: boolean;
  onCreateQuickReply: (quickReply: IndexableQuickReply) => Promise<void> | void;
  onDeleteQuickReplies: () => Promise<void>;
  onUpdateQuickReply: (quickReply: IndexableQuickReply) => Promise<void> | void;
  quickReplies: IndexableQuickReply[];
  selectedItems: string[];
  table: Table<IndexableQuickReply>;
  updatePermission: boolean;
};

export const QuickRepliesTable: FC<Props> = ({
  deletePermission,
  isLoading,
  isLoadingDeleteQuickReplies,
  isLoadingUpdateQuickReplies,
  onCreateQuickReply,
  onDeleteQuickReplies,
  onUpdateQuickReply,
  quickReplies,
  selectedItems,
  table,
  updatePermission,
}) => {
  const [isOpenQuickReplyModal, setIsOpenQuickReplyModal] = useState(false);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const [editQuickReply, setEditQuickReply] = useState<IndexableQuickReply>();

  const { t } = useTranslation();

  const onRowClicked = (row: Row<IndexableQuickReply>) => {
    setEditQuickReply(row.original);
    setIsOpenQuickReplyModal(true);
  };

  const closeQuickReplyModal = () => {
    setIsOpenQuickReplyModal(false);
    setEditQuickReply(undefined);
  };

  const renderAddButton = () =>
    updatePermission ? (
      <AddButton
        label={t('QUICK_REPLIES.ACTIONS.ADD')}
        onClick={() => {
          setIsOpenQuickReplyModal(true);
        }}
      />
    ) : null;

  const renderEmptyState = () => (
    <EmptyState
      button={renderAddButton}
      description={t('QUICK_REPLIES.EMPTY_STATE.DESCRIPTION')}
      title={t('QUICK_REPLIES.EMPTY_STATE.TITLE')}
    />
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Modal
        isOpen={isOpenQuickReplyModal || isOpenDeleteModal}
        onRequestClose={() => (isOpenQuickReplyModal ? closeQuickReplyModal() : setIsOpenDeleteModal(false))}
        style={customModalStyles}
      >
        {isOpenQuickReplyModal ? (
          <QuickReplyModal
            closeModal={() => closeQuickReplyModal()}
            isLoading={isLoadingUpdateQuickReplies}
            onSubmit={async quickReply => {
              if (editQuickReply) {
                await onUpdateQuickReply(quickReply);
              } else {
                await onCreateQuickReply(quickReply);
              }
              closeQuickReplyModal();
            }}
            quickReplies={quickReplies}
            quickReply={editQuickReply}
          />
        ) : null}
        {isOpenDeleteModal ? (
          <ConfirmationModal
            cancelButtonAction={() => setIsOpenDeleteModal(false)}
            cancelButtonLabel={t('SHARED.BUTTONS.CANCEL')}
            confirmButtonAction={async () => {
              await onDeleteQuickReplies();
              setIsOpenDeleteModal(false);
            }}
            confirmButtonLabel={t('SHARED.BUTTONS.DELETE')}
            loading={isLoadingDeleteQuickReplies}
            title={`${t('SHARED.BUTTONS.DELETE')} ${t('QUICK_REPLIES.LABEL', { count: selectedItems.length })}`}
          />
        ) : null}
      </Modal>

      <TableLayout
        addButtonAction={() => setIsOpenQuickReplyModal(true)}
        addButtonLabel={t('QUICK_REPLIES.ACTIONS.ADD')}
        deleteButtonAction={() => setIsOpenDeleteModal(true)}
        deleteButtonDisabled={selectedItems?.length <= 0}
        emptyState={renderEmptyState}
        isAddButtonPresent={updatePermission}
        isDeleteButtonPresent={deletePermission}
        onRowClicked={onRowClicked}
        rowClickAllowed={updatePermission}
        table={table}
      />
    </>
  );
};
